/**
 * OfficialPhaseSection — section collapsible de una fase del proyecto en el
 * Official Browser.
 *
 * Comportamiento:
 *  - Header con número de phase + nombre + status descriptivo + counter.
 *  - Si la phase tiene artifacts oficiales: tabla colapsable (default abierta
 *    cuando aprobada/in_progress, default cerrada cuando pending).
 *  - Si la phase está en progreso pero hay drafts/in-review (no oficial
 *    todavía), nota debajo de la tabla con link al tab Lifecycle.
 *  - Phases skipped/client_provided-pendiente con estilo distinto (border
 *    dashed + opacity reducida) y sin tabla.
 *
 * Acción "Ver" navega al official viewer (`/projects/$id/official/$artifactId`)
 * desde B.6.a. "Descargar PDF" quedó habilitada en la OT-0027 — no se
 * muestra en documentos aportados como archivo (`source_mode = 'upload'`),
 * donde el backend responde 409 y la descarga es la del original.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  Info,
  Loader2,
} from 'lucide-react'
import i18n from 'i18next'

import type {
  OfficialArtifact,
  ProjectPhase,
} from '../../api/projects'
import { useArtifactPdfDownload } from '../../hooks/useArtifactPdfDownload'
import { isSupportedLocale, toIntlLocale } from '../../i18n/config'
import { bySubphaseOrder } from './subphaseOrder'

interface OfficialPhaseSectionProps {
  phase: ProjectPhase
  artifacts: OfficialArtifact[]
  /** Total de artifacts de la phase (para el aviso "X en proceso").
   * Calculado desde phase.artifact_counts. */
  inFlightCount: number
  projectId: string
  /** True si el filtro global de search dejó esta phase sin matches —
   * mostrar header pero tabla vacía con una nota suave. Cuando el
   * orchestrator filtra los artifacts antes de pasarlos, este prop
   * permite distinguir "phase sin oficiales" vs "phase con oficiales pero
   * filtrado fuera". */
  filteredOut?: boolean
}

interface ArtifactTableProps {
  artifacts: OfficialArtifact[]
  projectId: string
  subphases: ProjectPhase['subphases']
}

export function OfficialPhaseSection({
  phase,
  artifacts,
  inFlightCount,
  projectId,
  filteredOut = false,
}: OfficialPhaseSectionProps) {
  const { t } = useTranslation('projects')
  // El backend ya resolvió el nombre del lifecycle correcto + i18n.
  const phaseName = phase.name || phase.phase_code

  const isSkipped = phase.contract_mode === 'skipped'
  // 'external_canon' is the literal API enum value; UI labels as "client-provided".
  const isClientProvidedPending =
    phase.contract_mode === 'external_canon' && phase.status !== 'approved'
  const isApproved = phase.status === 'approved'
  const isInProgress = phase.status === 'in_progress'
  const isBlocked = phase.status === 'blocked'

  // Open por default si la phase está activa o aprobada, cerrada en pending/skipped.
  const defaultOpen = isApproved || isInProgress
  const [open, setOpen] = useState<boolean>(defaultOpen)

  const officialCount = artifacts.filter((a) => a.is_canon).length

  // Estilos del wrapper según estado
  const wrapperClass =
    isSkipped || isClientProvidedPending
      ? 'mb-3 bg-surface border border-line border-dashed rounded-lg overflow-hidden opacity-70'
      : isInProgress
        ? 'mb-6 bg-surface border border-review/40 rounded-lg overflow-hidden'
        : 'mb-6 bg-surface border border-line rounded-lg overflow-hidden'

  const headerClass = isInProgress
    ? 'px-5 py-3 border-b border-line bg-review-soft/40 flex items-center gap-3'
    : isApproved
      ? 'px-5 py-3 border-b border-line bg-surface-muted flex items-center gap-3'
      : 'px-5 py-3 flex items-center gap-3'

  const numberClass = isApproved
    ? 'w-7 h-7 rounded-full bg-official text-canvas grid place-items-center font-display text-xs font-semibold shrink-0'
    : isInProgress
      ? 'w-7 h-7 rounded-full bg-review text-canvas grid place-items-center font-display text-xs font-semibold shrink-0'
      : isBlocked
        ? 'w-7 h-7 rounded-full bg-critical text-canvas grid place-items-center font-display text-xs font-semibold shrink-0'
        : isSkipped || isClientProvidedPending
          ? 'w-7 h-7 rounded-full bg-surface-muted border-2 border-dashed border-line-strong text-ink-subtle grid place-items-center font-display text-xs font-semibold shrink-0'
          : 'w-7 h-7 rounded-full bg-canvas border-2 border-line-strong text-ink-muted grid place-items-center font-display text-xs font-semibold shrink-0'

  // Subtitle según status + contract_mode
  const subtitle = useMemo(() => {
    if (isSkipped) return t('official.phase_header.skipped')
    if (isClientProvidedPending)
      return t('official.phase_header.client_provided_pending')
    if (isApproved) return t('official.phase_header.approved')
    if (isInProgress) {
      const total = sumPhaseArtifactTotal(phase)
      const approved = phase.artifact_counts.approved ?? 0
      return t('official.phase_header.in_progress', {
        approved,
        total,
        gate: phase.gate_description ?? '—',
      })
    }
    if (isBlocked) {
      return t('official.phase_header.blocked', {
        gate: phase.gate_description ?? '—',
      })
    }
    return t('official.phase_header.not_started', {
      gate: phase.gate_description ?? '—',
    })
  }, [phase, isSkipped, isClientProvidedPending, isApproved, isInProgress, isBlocked, t])

  const showCollapseToggle =
    !isSkipped && !isClientProvidedPending && artifacts.length > 0
  const counterColor = officialCount > 0 ? 'text-official font-medium' : 'text-ink-subtle'

  return (
    <section className={wrapperClass}>
      <header className={headerClass}>
        <div className={numberClass}>{phase.phase_order}</div>
        <div className="flex-1 min-w-0">
          <p
            className={
              isSkipped || isClientProvidedPending
                ? 'text-sm font-medium text-ink-muted'
                : 'text-sm font-semibold text-ink'
            }
          >
            {phaseName}
            {isInProgress ? (
              <span className="ml-2 text-[10px] font-medium uppercase tracking-wider text-review">
                · en curso
              </span>
            ) : null}
          </p>
          <p className="text-[10px] text-ink-subtle truncate">{subtitle}</p>
        </div>
        <span className={`text-xs ${counterColor}`}>
          {officialCount === 0
            ? t('official.phase_zero_official')
            : t('official.phase_counter', { count: officialCount })}
        </span>
        {showCollapseToggle ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? 'Collapse' : 'Expand'}
            className="p-1 rounded hover:bg-surface text-ink-muted"
          >
            {open ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        ) : null}
      </header>

      {open && artifacts.length > 0 ? (
        <ArtifactTable
          artifacts={artifacts}
          projectId={projectId}
          subphases={phase.subphases}
        />
      ) : null}

      {open && artifacts.length === 0 && filteredOut ? (
        <div className="px-5 py-6 text-center text-xs text-ink-subtle italic">
          —
        </div>
      ) : null}

      {/* Nota de "X en proceso" para phases activas con drafts/in_review */}
      {isInProgress && inFlightCount > 0 ? (
        <div className="px-5 py-2.5 border-t border-line bg-review-soft/20 text-xs text-ink-muted flex items-center gap-2 flex-wrap">
          <Info className="w-3.5 h-3.5 text-review shrink-0" />
          <span>
            {t('official.in_lifecycle_note', { count: inFlightCount })}
          </span>
          <Link
            to="/projects/$projectId/lifecycle"
            params={{ projectId }}
            className="ml-auto text-brand hover:underline flex items-center gap-1"
          >
            {t('official.go_to_lifecycle')}
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      ) : null}
    </section>
  )
}

// ─── Tabla de artifacts ────────────────────────────────────────────────────
function ArtifactTable({ artifacts, projectId, subphases }: ArtifactTableProps) {
  // Orden de las subfases del ciclo, no del texto del código (OT-0006).
  const sorted = useMemo(
    () => [...artifacts].sort(bySubphaseOrder(subphases)),
    [artifacts, subphases],
  )
  return (
    <table className="w-full text-sm">
      <tbody className="divide-y divide-line">
        {sorted.map((a) => (
          <ArtifactRow key={a.id} artifact={a} projectId={projectId} />
        ))}
      </tbody>
    </table>
  )
}

function ArtifactRow({
  artifact,
  projectId,
}: {
  artifact: OfficialArtifact
  projectId: string
}) {
  const { t } = useTranslation('projects')
  const { download, isDownloading } = useArtifactPdfDownload()

  const isOfficial = artifact.is_canon
  const versionBadge = isOfficial
    ? t('official.version_official', { n: artifact.current_version_number ?? 0 })
    : t('official.version_superseded', {
        n: artifact.current_version_number ?? 0,
      })
  const versionClass = isOfficial
    ? 'bg-official-soft text-official'
    : 'bg-surface-muted text-ink-subtle line-through'

  const hashShort = artifact.current_version_hash
    ? truncateHash(artifact.current_version_hash)
    : '—'

  return (
    <tr className="hover:bg-surface-muted">
      <td className="px-5 py-2.5 w-12 text-ink-subtle">
        <FileText className="w-4 h-4" />
      </td>
      <td className="px-3 py-2.5">
        <div className="font-medium text-ink">{artifact.name}</div>
        {artifact.code ? (
          <div className="text-[10px] font-mono text-ink-subtle">
            {artifact.code}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2.5 text-ink-muted text-xs font-mono">
        {artifact.subphase_code ?? '—'}
      </td>
      <td className="px-3 py-2.5">
        <span
          className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${versionClass}`}
        >
          {versionBadge}
        </span>
      </td>
      <td className="px-3 py-2.5 text-ink-muted text-xs font-mono">
        {hashShort}
      </td>
      <td className="px-3 py-2.5 text-ink-muted text-xs">
        {artifact.current_version_approved_at
          ? formatDate(artifact.current_version_approved_at)
          : '—'}
      </td>
      <td className="px-3 py-2.5 text-right whitespace-nowrap">
        {/* "Ver" enabled desde B.6.a → official viewer real */}
        <Link
          to="/projects/$projectId/official/$artifactId"
          params={{ projectId, artifactId: artifact.id }}
          className="text-xs text-brand hover:underline px-1"
        >
          {t('official.row.view')}
        </Link>
        {/* Los documentos aportados como archivo no sintetizan PDF: el
            backend responde 409 y su descarga es la del original, que
            vive en el visor. No se ofrece aquí una acción que se
            rechazaría (OT-0009). */}
        {artifact.source_mode === 'upload' || !artifact.current_version_id ? null : (
          <button
            type="button"
            onClick={() =>
              void download({
                artifactId: artifact.id,
                versionId: artifact.current_version_id as string,
                fallbackFilename: `${artifact.code ?? artifact.name}.pdf`,
              })
            }
            disabled={isDownloading}
            title={t('official.row.download')}
            aria-label={t('official.row.download')}
            className="text-xs text-ink-subtle hover:text-ink px-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDownloading ? (
              <Loader2 className="w-3.5 h-3.5 inline animate-spin" aria-hidden="true" />
            ) : (
              <Download className="w-3.5 h-3.5 inline" aria-hidden="true" />
            )}
          </button>
        )}
      </td>
    </tr>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function truncateHash(hash: string): string {
  if (hash.length <= 9) return hash
  return `${hash.slice(0, 4)}…${hash.slice(-4)}`
}

function formatDate(iso: string): string {
  const locale = toIntlLocale(
    isSupportedLocale(i18n.language) ? i18n.language : 'es',
  )
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

function sumPhaseArtifactTotal(phase: ProjectPhase): number {
  const c = phase.artifact_counts
  return (
    (c.draft ?? 0) +
    (c.in_review ?? 0) +
    (c.approved ?? 0) +
    (c.superseded ?? 0)
  )
}
